import { Box, Card, Collapsible, Flex, Text, VStack } from '@chakra-ui/react';
import { CriticalSystem } from '@/types/CriticalInfo';
import SlotActionButton from '@/components/common/cards/SlotActionButton';
import SlotRemoveButton from '@/components/common/cards/SlotRemoveButton';
import {
	EASE_DRAWER,
	EASE_OUT,
	TRANSITION_FORM_REVEAL_CLOSE,
	TRANSITION_FORM_REVEAL_OPEN,
} from '@/components/ui/motion';
import SystemForm from './SystemForm';
import SystemRowSummary from './SystemRowSummary';
import { SLOT_CARD_ICON_TARGET_SIZE } from '@/components/common/cards/slotCardStyles';
import { useSystemGroupCardState } from '@/hooks/house/critical-info/useSystemGroupCardState';

interface SystemGroupCardProps {
	categoryName: string;
	systems: CriticalSystem[];
	isFilled: boolean;
	expandedSystem: string | null;
	addingSystemName?: string | null;
	onToggleSystem: (systemName: string | null) => void;
	onStartAdding: () => void;
	onFinishAdding?: () => void;
	onCancelEdit: (systemName: string) => void;
	onDoneSystem?: (systemName: string) => void;
	onRemoveSystem?: (systemName: string) => Promise<boolean | void> | boolean | void;
	onUpdateField: (systemName: string, fieldName: string, value: string) => void;
	dirtySystemNames?: Set<string>;
	savingSystemName?: string | null;
	isSaving?: boolean;
	onSaveSystem?: (systemName: string) => Promise<boolean | void> | boolean | void;
	animateBodyEntry?: boolean;
	collapseCancelsSystemNames?: Set<string>;
	canEdit?: boolean;
	canRemove?: boolean;
}

const BODY_ENTER_ANIMATION_CSS = {
	display: 'grid',
	animation: 'criticalInfoBodyEnter 180ms ease-out both',
	'@keyframes criticalInfoBodyEnter': {
		from: { gridTemplateRows: '0fr', opacity: 0.96 },
		to: { gridTemplateRows: '1fr', opacity: 1 },
	},
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
} as const;

const PRIMARY_FORM_MAX_HEIGHT = '520px';
const SYSTEM_ROW_HEIGHT = '64px';
const PRIMARY_DONE_TRANSITION = `max-height 200ms ${EASE_OUT}, opacity 120ms ${EASE_OUT}`;

const SystemGroupCard = (props: SystemGroupCardProps) => {
	const { categoryName, animateBodyEntry = false, canEdit = true } = props;
	const {
		primarySystem,
		addingSystem,
		rows,
		primaryRow,
		documentedCount,
		addFormRef,
		primaryFormRef,
		isCompletingPrimary,
		isHeaderFilled,
		isPrimaryFormOpen,
		isAddFormOpen,
		showPrimaryBlock,
		showEmptyHeaderLine,
		showPrimaryTooltipLine,
		headerSecondaryText,
		addFormHeading,
		showRemoveEmptySlot,
		handleAddButtonClick,
		handleHeaderClick,
		handleHeaderKeyDown,
		handleRemoveEmptySlot,
		handleToggleRow,
		handlePrimaryFormTransitionEnd,
		handleAddFormTransitionEnd,
		getFormProps,
	} = useSystemGroupCardState({ ...props, canEdit });
	const cardBackground = isHeaderFilled || isPrimaryFormOpen ? 'surface.default' : 'surface.canvas';

	return (
		<Card.Root
			variant="outline"
			borderColor="border.default"
			borderStyle={isHeaderFilled || isPrimaryFormOpen ? 'solid' : 'dashed'}
			borderWidth={isHeaderFilled || isPrimaryFormOpen ? '1px' : '1.5px'}
			borderRadius="3xl"
			bg={cardBackground}
			boxShadow={isHeaderFilled || isPrimaryFormOpen ? 'sm' : 'none'}
			overflow="hidden"
			transition={`border-color 160ms ease, box-shadow 300ms ${EASE_DRAWER}`}
			_hover={
				canEdit && !isHeaderFilled && !isPrimaryFormOpen
					? { borderColor: 'border.strong', boxShadow: 'xs' }
					: undefined
			}
		>
			<Card.Header
				p={4}
				bg={cardBackground}
				borderTopRadius="3xl"
				cursor={canEdit && !isHeaderFilled && !isPrimaryFormOpen ? 'pointer' : 'default'}
				role={canEdit && !isHeaderFilled && !isPrimaryFormOpen ? 'button' : undefined}
				tabIndex={canEdit && !isHeaderFilled && !isPrimaryFormOpen ? 0 : undefined}
				onClick={handleHeaderClick}
				onKeyDown={handleHeaderKeyDown}
			>
				<Flex
					justify="space-between"
					align={isPrimaryFormOpen ? 'flex-start' : 'center'}
					minH={SLOT_CARD_ICON_TARGET_SIZE}
				>
					<Box minW={0}>
						<Text
							fontSize="lg"
							fontWeight="semibold"
							color={isHeaderFilled || isPrimaryFormOpen ? 'fg' : 'fg.muted'}
							truncate
						>
							{categoryName}
							{documentedCount > 1 && (
								<Text as="span" textStyle="body.sm" fontWeight="semibold" ml={2}>
									({documentedCount})
								</Text>
							)}
						</Text>
						<Box
							display="grid"
							gridTemplateRows={showEmptyHeaderLine || showPrimaryTooltipLine ? '1fr' : '0fr'}
							opacity={showEmptyHeaderLine || showPrimaryTooltipLine ? 1 : 0}
							transition={`grid-template-rows 160ms ${EASE_OUT}, opacity 120ms ${EASE_OUT}`}
						>
							<Box overflow="hidden">
								<Text
									textStyle="body.sm"
									color={showPrimaryTooltipLine ? 'fg.muted' : 'fg.subtle'}
									mt={showPrimaryTooltipLine ? 1 : 0}
									truncate={!showPrimaryTooltipLine}
								>
									{headerSecondaryText}
								</Text>
							</Box>
						</Box>
					</Box>
					{canEdit && (
						<Flex flexShrink={0} gap={1} pt={isPrimaryFormOpen ? 0.5 : 0}>
							{showRemoveEmptySlot && (
								<SlotRemoveButton
									label={primarySystem.name}
									testId={`remove-${primarySystem.name}`}
									onRemove={handleRemoveEmptySlot}
								/>
							)}
							<SlotActionButton
								ariaLabel={
									isAddFormOpen || isPrimaryFormOpen
										? `Cancel ${categoryName}`
										: `Add ${categoryName}`
								}
								tooltipLabel={
									isAddFormOpen || isPrimaryFormOpen
										? `Cancel ${categoryName}`
										: documentedCount > 0
											? `Add another ${categoryName}`
											: `Add ${categoryName}`
								}
								isCancel={isAddFormOpen || isPrimaryFormOpen}
								onClick={handleAddButtonClick}
							/>
						</Flex>
					)}
				</Flex>
			</Card.Header>
			{(isHeaderFilled || isPrimaryFormOpen) && <Box borderTop="1px solid" borderColor="border.default" />}

			<Card.Body
				p={0}
				bg="surface.default"
				borderBottomRadius="3xl"
				css={animateBodyEntry ? BODY_ENTER_ANIMATION_CSS : undefined}
			>
				<VStack gap={0} align="stretch" overflow="hidden">
					{rows.map((row) => {
						return (
							<Box key={row.system.name}>
								<Box
									overflow="hidden"
									borderBottom={!row.isLast ? '1px solid' : 'none'}
									borderColor="border.default"
									bg="surface.default"
									borderBottomRadius={row.isLast ? '3xl' : '0'}
								>
									<Flex align="center" justify="space-between" gap={3} minH="40px">
										<Box
											px={4}
											py={3}
											minH="64px"
											cursor="pointer"
											role="button"
											tabIndex={0}
											aria-label={`Open ${row.system.name}`}
											onClick={() => handleToggleRow(row.system.name)}
											onKeyDown={(event) => {
												if (event.key !== 'Enter' && event.key !== ' ') return;
												event.preventDefault();
												handleToggleRow(row.system.name);
											}}
											_hover={{ bg: 'surface.subtle' }}
											_focusVisible={{
												outline: '2px solid',
												outlineColor: 'focus.ring',
												outlineOffset: '-2px',
											}}
											transition="background-color 0.15s ease"
											w="full"
										>
											<SystemRowSummary
												rowLabel={row.rowLabel}
												notesSummary={row.notesSummary}
												hasNotes={row.hasNotes}
												isExpanded={row.isExpanded}
											/>
										</Box>
									</Flex>

									<Collapsible.Root open={row.isExpanded}>
										<Collapsible.Content animationDuration="moderate">
											<Box
												borderTop="1px solid"
												borderColor="border.default"
												p={4}
												bg="surface.default"
											>
												<SystemForm {...getFormProps(row.system, 'row')} />
											</Box>
										</Collapsible.Content>
									</Collapsible.Root>
								</Box>
							</Box>
						);
					})}

					{showPrimaryBlock && (
						<Box
							overflow="hidden"
							display={isCompletingPrimary ? 'block' : 'grid'}
							gridTemplateRows={isCompletingPrimary ? undefined : isPrimaryFormOpen ? '1fr' : '0fr'}
							maxH={
								isPrimaryFormOpen
									? PRIMARY_FORM_MAX_HEIGHT
									: isCompletingPrimary
										? SYSTEM_ROW_HEIGHT
										: undefined
							}
							opacity={isPrimaryFormOpen || isCompletingPrimary ? 1 : 0}
							transition={
								isPrimaryFormOpen
									? TRANSITION_FORM_REVEAL_OPEN
									: isCompletingPrimary
										? PRIMARY_DONE_TRANSITION
										: TRANSITION_FORM_REVEAL_CLOSE
							}
							onTransitionEnd={handlePrimaryFormTransitionEnd}
						>
							<Box ref={primaryFormRef} bg="surface.default" overflow="hidden">
								{isCompletingPrimary && primaryRow ? (
									<Box position="relative">
										{/* Keeps the form's natural height in flow while the visible row overlay shrinks into place. */}
										<Box p={4} visibility="hidden" pointerEvents="none">
											<SystemForm {...getFormProps(primarySystem, 'primary')} />
										</Box>
										<Box
											position="absolute"
											inset={0}
											px={4}
											py={3}
											minH={SYSTEM_ROW_HEIGHT}
											bg="surface.default"
										>
											<SystemRowSummary
												rowLabel={primaryRow.rowLabel}
												notesSummary={primaryRow.notesSummary}
												hasNotes={primaryRow.hasNotes}
											/>
										</Box>
									</Box>
								) : (
									<Box p={4}>
										<SystemForm {...getFormProps(primarySystem, 'primary')} />
									</Box>
								)}
							</Box>
						</Box>
					)}

					<Box
						display="grid"
						gridTemplateRows={isAddFormOpen ? '1fr' : '0fr'}
						transition={isAddFormOpen ? TRANSITION_FORM_REVEAL_OPEN : TRANSITION_FORM_REVEAL_CLOSE}
						onTransitionEnd={handleAddFormTransitionEnd}
					>
						<Box overflow="hidden">
							<Box
								ref={addFormRef}
								borderTop="1px solid"
								borderColor="border.default"
								p={4}
								bg="surface.default"
							>
								{addingSystem && (
									<SystemForm {...getFormProps(addingSystem, 'add')} heading={addFormHeading} />
								)}
							</Box>
						</Box>
					</Box>
				</VStack>
			</Card.Body>
		</Card.Root>
	);
};

export default SystemGroupCard;
